import React, { useMemo, useState, useEffect } from "react";

import "./BlogList.css";

import { Link } from "react-router-dom";
import { getBlogPosts } from "../../../api/storefront";
import { getImageUrl } from "../../../api/client";
import Seo from "../../Seo/Seo";
import { useSiteSeo } from "../../../context/SiteSeoContext";
import { absoluteCanonicalUrl } from "../../../utils/siteUrl";
import { truncateMetaDescription } from "../../../utils/seoText";
import { usePrerenderReady } from "../../../utils/prerenderReady";

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("");
  const siteCtx = useSiteSeo();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBlogPosts({ limit: 50 })
      .then((rows) => {
        if (!cancelled) setPosts(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => {
      const c = p.category_label && String(p.category_label).trim();
      if (c) set.add(c.toUpperCase());
    });
    return ["ALL", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [posts]);

  const filtered = useMemo(() => {
    if (!activeCategory || activeCategory === "ALL") return posts;
    return posts.filter((p) => {
      const c = (p.category_label || "").trim().toUpperCase();
      return c === activeCategory;
    });
  }, [posts, activeCategory]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  usePrerenderReady(!loading);

  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  return (
    <>
      <Seo
        title={`Style tips & boutique news${titleSuffix}`}
        description={truncateMetaDescription(
          "Stories on outfit rental, sizing, celebrations, festivals, and new arrivals."
        )}
        canonicalUrl={absoluteCanonicalUrl("/blog")}
      />
      <div className="blogListSection">
        <div className="blogListHeaderContainer">
          <div className="blogListHeader">
            <h2>The Blog</h2>
            <div className="blogListHeaderCategories">
              {categories.map((cat) => {
                const isAll = cat === "ALL";
                const selected = isAll
                  ? activeCategory === "" || activeCategory === "ALL"
                  : activeCategory === cat;
                return (
                <p
                  key={cat}
                  className={selected ? "activeCategory" : ""}
                  onClick={() => {
                    setActiveCategory(isAll ? "ALL" : cat);
                  }}
                  role="presentation"
                  style={{ cursor: "pointer" }}
                >
                  {cat}
                </p>
                );
              })}
            </div>
          </div>
        </div>
        {loading ? (
          <p style={{ padding: "2rem", textAlign: "center" }}>Loading articles…</p>
        ) : filtered.length === 0 ? (
          <p style={{ padding: "2rem", textAlign: "center" }}>No posts yet.</p>
        ) : (
          <div className="blogPostListContainer">
            {filtered.map((blogPost) => (
              <div className="blogPost" key={blogPost.slug}>
                <div className="blogPostThumb">
                  {blogPost.featured_image_url ? (
                    <img
                      src={getImageUrl(blogPost.featured_image_url)}
                      alt={blogPost.title ? `${blogPost.title} — thumbnail` : "Blog post thumbnail"}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        minHeight: 200,
                        background: "linear-gradient(135deg, #f5f5f5, #eaeaea)",
                      }}
                      aria-hidden
                    />
                  )}
                </div>
                <div className="blogPostContent">
                  <div className="blogPostContentDate">
                    <p>{blogPost.author_name || "editor"}</p>
                    <p>
                      {blogPost.published_at
                        ? new Date(blogPost.published_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : blogPost.created_at
                          ? new Date(blogPost.created_at).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : ""}
                    </p>
                  </div>
                  <div className="blogPostContentHeading">
                    <Link to={`/blog/${encodeURIComponent(blogPost.slug)}`} onClick={scrollToTop}>
                      {blogPost.title}
                    </Link>
                  </div>
                  <div className="blogPostContentDescription">
                    <p>
                      {(blogPost.excerpt || "").trim() ||
                        "Read the latest from Gem Fashion rentals and style tips."}
                    </p>
                  </div>
                  <div className="blogPostContentReadMore">
                    <Link to={`/blog/${encodeURIComponent(blogPost.slug)}`} onClick={scrollToTop}>
                      Continue Reading
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default BlogList;
